import { StringObject } from "../../objects/StringObject";
import TransformNullifyTool from "../../tools/TransformNullifyTool";
import { ScrollScope, ScrollScopeRegistry } from "./ScrollScopeRegistry";

type CenterState = {
  cx: number;
  cy: number;
  width: number;
  height: number;
  valid: boolean;
  epochSum: number;
  scopeChain: ScrollScope[];
  ro?: ResizeObserver;
  el?: HTMLElement;
  nullifyTransform: boolean;
};

export type CenterRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

export class CenterCache {
  private map = new WeakMap<StringObject, CenterState>();
  private transformNullify = new TransformNullifyTool();

  constructor(private scopes: ScrollScopeRegistry) {}

  attach(obj: StringObject, options?: { nullifyTransform?: boolean }) {
    if (this.map.has(obj)) return;
    const el = obj.htmlElement;
    const st: CenterState = {
      cx: 0,
      cy: 0,
      width: 0,
      height: 0,
      valid: false,
      epochSum: -1,
      scopeChain: this.scopes.resolveChain(el),
      el,
      nullifyTransform: options?.nullifyTransform === true,
    };
    st.ro = new ResizeObserver(() => {
      st.valid = false;
    });
    st.ro.observe(el);
    this.map.set(obj, st);
  }

  detach(obj: StringObject) {
    const st = this.map.get(obj);
    if (!st) return;
    st.ro?.disconnect();
    this.map.delete(obj);
  }

  invalidate(obj: StringObject) {
    const st = this.map.get(obj);
    if (st) st.valid = false;
  }

  invalidateAll() {
    this.scopes.bumpRoot();
  }

  private ensureFresh(st: CenterState): void {
    const currentSum = ScrollScopeRegistry.sumEpochs(st.scopeChain);
    if (st.valid && st.epochSum === currentSum) return;
    if (!st.el) return;
    const r = st.nullifyTransform
      ? this.transformNullify.process({ element: st.el })
      : st.el.getBoundingClientRect();
    st.cx = r.left + r.width / 2;
    st.cy = r.top + r.height / 2;
    st.width = r.width;
    st.height = r.height;
    st.valid = true;
    st.epochSum = currentSum;
  }

  getCenter(obj: StringObject): { cx: number; cy: number } {
    const st = this.map.get(obj);
    if (!st || !st.el) return { cx: 0, cy: 0 };
    this.ensureFresh(st);
    return { cx: st.cx, cy: st.cy };
  }

  getRect(obj: StringObject): CenterRect | null {
    const st = this.map.get(obj);
    if (!st || !st.el) return null;
    this.ensureFresh(st);
    const halfW = st.width / 2;
    const halfH = st.height / 2;
    return {
      left: st.cx - halfW,
      right: st.cx + halfW,
      top: st.cy - halfH,
      bottom: st.cy + halfH,
      width: st.width,
      height: st.height,
    };
  }
}
